import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import client, { apiErr } from "@/lib/apiClient";
import { toast } from "sonner";
import { Zap, Loader2, ShieldCheck, Mail } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { loginWithPassword, completeMfa, requestEmailOtp, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null); // {challenge_id, setup?, totp_secret?, qr_data_url?, methods?}
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMethod, setMfaMethod] = useState("totp");
  const [playlist, setPlaylist] = useState(null); // null=loading, []=empty (fallback), [..]=playlist
  const [videoIdx, setVideoIdx] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = React.useRef();

  useEffect(() => {
    client.get("/login-videos")
      .then(({ data }) => setPlaylist(Array.isArray(data) ? data : []))
      .catch(() => setPlaylist([]));
  }, []);

  const advance = () => {
    setVideoIdx((i) => ((playlist && playlist.length) ? (i + 1) % playlist.length : 0));
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await loginWithPassword(form.email, form.password);
        if (res.mfa) {
          setMfa({ ...res.mfa, setup: !!res.mfa.mfa_setup_required });
          setMfaMethod("totp");
          setBusy(false);
          return;
        }
        toast.success("Welcome to AdGen Store!");
        navigate("/dashboard");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Welcome to AdGen Store!");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(apiErr(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await completeMfa({
        challenge_id: mfa.challenge_id,
        code: mfaCode.trim(),
        setup: mfa.setup,
        method: mfaMethod,
      });
      toast.success("Signed in.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErr(err, "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const sendEmailOtp = async () => {
    try {
      await requestEmailOtp(mfa.challenge_id);
      toast.success("A 6-digit code was generated. Check your email or backend log.");
      setMfaMethod("email");
    } catch (err) {
      toast.error(apiErr(err, "Could not send code"));
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-[#0B0C10] lg:block" data-testid="login-bg-panel">
        {/* Loading: keep the dark panel colour only — never flash the static
            image while we're still probing whether the admin has a playlist. */}
        {playlist === null && <div className="h-full w-full bg-[#0B0C10]" data-testid="login-bg-loading" />}
        {playlist !== null && playlist.length > 0 && (
          <video
            key={playlist[videoIdx]?.id || videoIdx}
            ref={videoRef}
            src={playlist[videoIdx]?.url}
            className="h-full w-full object-cover opacity-70"
            style={{ display: videoReady ? "block" : "none" }}
            autoPlay muted playsInline preload="auto"
            loop={playlist.length === 1}
            onCanPlay={() => setVideoReady(true)}
            onEnded={() => { if (playlist.length > 1) { setVideoReady(false); advance(); } }}
            data-testid="login-bg-video"
          />
        )}
        {/* Video-buffering placeholder while the next clip is loading — same
            panel colour so there's no perceived flash. */}
        {playlist !== null && playlist.length > 0 && !videoReady && (
          <div className="h-full w-full bg-[#0B0C10]" data-testid="login-bg-buffer" />
        )}
        {/* Only render the static image if the admin has zero videos. */}
        {playlist !== null && playlist.length === 0 && (
          <img src="https://images.unsplash.com/photo-1674305281997-b6538532f388?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="art" className="h-full w-full object-cover opacity-60" data-testid="login-bg-image" />
        )}
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 text-white">
            <img src="/adgen-logo-v2.png" alt="AdGen Store" className="h-9 w-9 rounded-md object-contain" draggable={false} />
            <span className="font-display text-xl font-extrabold">AdGen Store</span>
          </div>
          <p className="mt-4 max-w-md font-display text-3xl font-bold text-white">Create. Advertise. Grow.</p>
          <p className="mt-2 max-w-md text-white/70">Professional AI creatives in seconds — join thousands of creators & businesses.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-2 lg:hidden">
            <img src="/adgen-logo-v2.png" alt="AdGen Store" className="h-8 w-8 rounded-md object-contain" draggable={false} />
            <span className="font-display text-lg font-extrabold">AdGen Store</span>
          </button>

          {mfa ? (
            <div data-testid="mfa-form">
              <div className="mb-2 flex items-center gap-2 text-primary"><ShieldCheck className="h-5 w-5" /><h1 className="text-2xl font-bold">Two-factor authentication</h1></div>
              {mfa.setup ? (
                <>
                  <p className="mt-1 text-sm text-muted-foreground">First-time admin login — scan the QR with an authenticator app (Google Authenticator, Authy) and enter the 6-digit code to finish setup.</p>
                  {mfa.qr_data_url && (
                    <img src={mfa.qr_data_url} alt="TOTP QR" className="mt-4 h-44 w-44 rounded-lg border" data-testid="mfa-qr" />
                  )}
                  <p className="mt-2 break-all text-[11px] text-muted-foreground">Or enter this key manually: <span className="font-mono">{mfa.totp_secret}</span></p>
                </>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app, or request a code by email.</p>
              )}

              <form onSubmit={verifyMfa} className="mt-5 space-y-4">
                <div>
                  <Label htmlFor="mfa-code">6-digit code</Label>
                  <Input id="mfa-code" data-testid="mfa-code-input" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                         value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="123 456" required className="mt-1.5 tracking-widest" />
                  {!mfa.setup && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground">Verifying via: <span className="font-semibold">{mfaMethod === "email" ? "Email OTP" : "Authenticator app"}</span></p>
                  )}
                </div>
                <Button type="submit" data-testid="mfa-submit-button" disabled={busy || mfaCode.length < 6} className="w-full rounded-full">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify & continue
                </Button>
              </form>

              {!mfa.setup && (
                <Button variant="outline" data-testid="mfa-email-request" onClick={sendEmailOtp} className="mt-3 w-full gap-2 rounded-full">
                  <Mail className="h-4 w-4" /> Send code to email instead
                </Button>
              )}

              <button type="button" onClick={() => { setMfa(null); setMfaCode(""); }} className="mt-4 w-full text-center text-xs text-muted-foreground underline">
                Use a different account
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold">Welcome</h1>
              <p className="mt-1 text-sm text-muted-foreground">Sign in to start creating.</p>

              <Button data-testid="google-login-button" onClick={googleLogin} variant="outline" className="mt-6 w-full gap-2 rounded-full">
                <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
                Continue with Google
              </Button>

              <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" /> or with email <div className="h-px flex-1 bg-border" />
              </div>

              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Sign up</TabsTrigger>
                </TabsList>
                <form onSubmit={submit} className="mt-5 space-y-4">
                  {mode === "register" && (
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" placeholder="Your name" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid="auth-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" placeholder="you@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" data-testid="auth-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5" placeholder="••••••••" />
                  </div>
                  <Button type="submit" data-testid="auth-submit-button" disabled={busy} className="w-full rounded-full">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {mode === "login" ? "Log in" : "Create account"}
                  </Button>
                </form>
              </Tabs>
              <p className="mt-6 text-center text-xs text-muted-foreground" data-testid="login-consent-text">
                By continuing with Google or Email, you agree to our{" "}
                <Link to="/terms" className="text-primary underline-offset-4 hover:underline" data-testid="login-consent-terms">Terms of Service</Link>
                {" "}and acknowledge that you have read and understand our{" "}
                <Link to="/privacy" className="text-primary underline-offset-4 hover:underline" data-testid="login-consent-privacy">Privacy Policy</Link>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
